// Copyright (c) 2021 WiseTime. All rights reserved.

import { isTimelineCachedForDate, selectActiveDate } from "../../slices/timeline"
import {
  Clock,
  getDatesOfWeek,
  TimelineDate,
  toDate,
  DateRange,
  toTimelineDate,
} from "../../dateTime"
import { RootState } from "../../../../app/store"
import { DateTime } from "luxon"

// Describes how we should fetch timeline and day summary data.
export type TimelineFetchOperations = {
  // The requested timeline should be fetched as soon as possible.
  // The whole fetch operation should fail if this fails.
  requestedTimeline: TimelineDate,
  // Fetch extra timelines after we have received the requested timeline.
  // Extra timelines may be fetched in parallel, but only after we have
  // received the requested timeline. This minimises network contention.
  // The whole fetch operation should NOT fail if this fails.
  extraTimelines: DateRange[],
  // Day summaries that we should fetch in parallel with the requested timeline.
  // The whole fetch operation should fail if this fails.
  daySummaries: DateRange,
}

const isActiveWeekCached = (state: RootState): boolean => {
  const activeDate = selectActiveDate(state)
  const localDatesOfWeek = getDatesOfWeek(toDate(activeDate))
  return localDatesOfWeek.some(isTimelineCachedForDate(state))
}

const modifyTimelineDate = (f: (dateTime: DateTime) => DateTime, timelineDate: TimelineDate): TimelineDate =>
  toTimelineDate(f(toDate(timelineDate)))

const minus1 = (timelineDate: TimelineDate): TimelineDate =>
  modifyTimelineDate(d => d.minus({ day: 1 }), timelineDate)

const plus1 = (timelineDate: TimelineDate): TimelineDate =>
  modifyTimelineDate(d => d.plus({ day: 1 }), timelineDate)

// To minimise the number of fetches, the following behaviour is implemented:
//
//   - If active date is at the beginning or the end of the week, return one range
//   - If active date is today or in the future, return the range before the active date
//   - Otherwise return two time ranges: One before and one after the active date
//
// The active date is not included in the returned range(s).
export const extraTimelineRanges = (clock: Clock) => (activeDate: TimelineDate): DateRange[] => {

  const localDatesOfWeek = getDatesOfWeek(toDate(activeDate))
  const [monday, tuesday, , , , saturday, sunday] = localDatesOfWeek
  if (monday === undefined || tuesday === undefined || saturday === undefined || sunday === undefined) {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    throw "getDatesOfWeek didn't produce all 7 days as expected"
  }

  if (activeDate >= toTimelineDate(clock())) {
    // Active date is today or later. We only need before range.
    return [{
      startDateLocal: monday,
      endDateLocal: minus1(activeDate),
    }]
  } else if (localDatesOfWeek[0] === activeDate) {
    // Active date is first day of the week. We only need to return Tue to Sun.
    return [{
      startDateLocal: tuesday,
      endDateLocal: sunday,
    }]
  } else if (localDatesOfWeek[6] === activeDate) {
    // Active date is last day of the week. We only need to return Mon to Sat.
    return [{
      startDateLocal: monday,
      endDateLocal: saturday,
    }]
  } else {
    // Active date is in the middle of the week. Return before and after ranges.
    return [{
      startDateLocal: monday,
      endDateLocal: minus1(activeDate),
    },
    {
      startDateLocal: plus1(activeDate),
      endDateLocal: sunday,
    }]
  }
}

// If requestedTimeline is undefined we should fetch the timeline for the currently active date.
export const planTimelineFetchOperations =
  (state: RootState, clock: Clock) =>
    (requestedTimeline: TimelineDate | undefined): TimelineFetchOperations => {

      if (requestedTimeline !== undefined) {
        // We only want to fetch the timeline for the requested date.
        return {
          requestedTimeline,
          extraTimelines: [],
          daySummaries: { startDateLocal: requestedTimeline, endDateLocal: requestedTimeline }
        }
      }

      const activeDate = selectActiveDate(state)

      if (isActiveWeekCached(state)) {
        // Only fetch the timeline and day summary for the currently active date.
        return {
          requestedTimeline: activeDate,
          // We probably already have data for the whole week.
          extraTimelines: [],
          daySummaries: { startDateLocal: activeDate, endDateLocal: activeDate }
        }
      }

      // Fetch the timeline for the currently active date.
      // Fetch timelines for the rest of the week. Don't include future time.
      // Fetch Day summaries for the whole week.
      const localDatesOfActiveWeek = getDatesOfWeek(toDate(activeDate))
      if (localDatesOfActiveWeek[0] === undefined || localDatesOfActiveWeek[6] === undefined) {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        throw "getDatesOfWeek didn't produce all 7 days as expected"
      }
      return {
        requestedTimeline: activeDate,
        extraTimelines: extraTimelineRanges(clock)(activeDate),
        daySummaries: {
          startDateLocal: localDatesOfActiveWeek[0],
          endDateLocal: localDatesOfActiveWeek[6],
        }
      }
    }
